import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { routes } from '../routes';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchCycles, createCycle, updateCycle, deleteCycle,
  activateCycle, lockCycle, closeCycle,
  fetchTeamScorecards, upsertTeamScorecard, deleteTeamScorecard,
} from '../services/reviewCycleService';
import { fetchTemplates } from '../services/reviewTemplateService';
import type { ReviewCycle, ReviewTemplate, TeamScorecard, CyclePeriod, CycleStatus } from '../types';
import { Timestamp } from 'firebase/firestore';

const STATUS_NEXT: Partial<Record<CycleStatus, { label: string; action: string }>> = {
  draft: { label: 'Activate', action: 'active' },
  active: { label: 'Lock', action: 'locked' },
  locked: { label: 'Close', action: 'closed' },
};

const STATUS_COLOR: Record<CycleStatus, string> = {
  draft: 'bg-gray-100 text-gray-600',
  active: 'bg-emerald-50 text-emerald-700',
  locked: 'bg-amber-50 text-amber-700',
  closed: 'bg-gray-100 text-gray-400',
};

const CycleManagement: React.FC = () => {
  const { user } = useAuth();
  const [cycles, setCycles] = useState<ReviewCycle[]>([]);
  const [templates, setTemplates] = useState<ReviewTemplate[]>([]);
  const [selectedCycleId, setSelectedCycleId] = useState<string | null>(null);
  const [scorecards, setScorecards] = useState<TeamScorecard[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editCycle, setEditCycle] = useState<ReviewCycle | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [activateResult, setActivateResult] = useState<string | null>(null);

  // Form state
  const [form, setForm] = useState({
    name: '',
    year: new Date().getFullYear(),
    period: 'H1' as CyclePeriod,
    templateId: '',
    prepWindowStart: '',
    startDate: '',
    lockDate: '',
    endDate: '',
    frameworkVersion: 'optics' as 'optics' | 'hr_v1',
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedCycleId) loadScorecards(selectedCycleId);
  }, [selectedCycleId]);

  const loadData = async () => {
    try {
      const [c, t] = await Promise.all([fetchCycles(), fetchTemplates()]);
      setCycles(c);
      setTemplates(t);
    } catch (e) {
      setError(String(e));
    }
  };

  const loadScorecards = async (cycleId: string) => {
    try {
      const sc = await fetchTeamScorecards(cycleId);
      setScorecards(sc);
    } catch (e) {
      setError(String(e));
    }
  };

  const resetForm = () => {
    setForm({ name: '', year: new Date().getFullYear(), period: 'H1', templateId: '', prepWindowStart: '', startDate: '', lockDate: '', endDate: '', frameworkVersion: 'optics' });
    setEditCycle(null);
    setShowForm(false);
  };

  const openCreate = () => {
    setForm({ name: '', year: new Date().getFullYear(), period: 'H1', templateId: templates[0]?.id ?? '', prepWindowStart: '', startDate: '', lockDate: '', endDate: '', frameworkVersion: 'optics' });
    setEditCycle(null);
    setShowForm(true);
  };

  const openEdit = (c: ReviewCycle) => {
    setEditCycle(c);
    setForm({
      name: c.name,
      year: c.year,
      period: c.period,
      templateId: c.templateId,
      prepWindowStart: tsToInput(c.prepWindowStart),
      startDate: tsToInput(c.startDate),
      lockDate: tsToInput(c.lockDate),
      endDate: tsToInput(c.endDate),
      frameworkVersion: c.frameworkVersion ?? 'optics',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    // For hr_v1 cycles, templateId defaults to 'default-hr-v1' below — no
    // template selection required from the OPTICS template list.
    const needsTemplate = form.frameworkVersion === 'optics';
    if (!form.name || (needsTemplate && !form.templateId) || !form.startDate || !form.endDate) {
      setError(needsTemplate
        ? 'Name, template, start date and end date are required.'
        : 'Name, start date and end date are required.');
      return;
    }
    if (form.frameworkVersion === 'hr_v1' && !form.templateId) {
      form.templateId = 'default-hr-v1';
    }
    setBusy(true);
    setError('');
    try {
      const payload = {
        name: form.name,
        year: form.year,
        period: form.period,
        templateId: form.templateId,
        status: editCycle?.status ?? 'draft' as CycleStatus,
        prepWindowStart: Timestamp.fromDate(new Date(form.prepWindowStart || form.startDate)),
        startDate: Timestamp.fromDate(new Date(form.startDate)),
        lockDate: Timestamp.fromDate(new Date(form.lockDate || form.endDate)),
        endDate: Timestamp.fromDate(new Date(form.endDate)),
        createdBy: user!.id,
        frameworkVersion: form.frameworkVersion,
        ...(form.frameworkVersion === 'hr_v1'
          ? { hrFrameworkTemplateId: 'default-hr-v1' }
          : {}),
      };
      if (editCycle) {
        await updateCycle(editCycle.id, payload);
      } else {
        await createCycle(payload);
      }
      await loadData();
      resetForm();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  const handleTransition = async (cycle: ReviewCycle) => {
    const next = STATUS_NEXT[cycle.status];
    if (!next) return;
    if (!confirm(`${next.label} cycle "${cycle.name}"?`)) return;
    setBusy(true);
    setActivateResult(null);
    try {
      if (next.action === 'active') {
        const result = await activateCycle(cycle.id, user!.id);
        setActivateResult(`Activated. ${result.formsCreated} review forms created.`);
      } else if (next.action === 'locked') {
        await lockCycle(cycle.id);
      } else if (next.action === 'closed') {
        await closeCycle(cycle.id);
      }
      await loadData();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteCycle = async (id: string) => {
    if (!confirm('Delete this cycle? This cannot be undone.')) return;
    setBusy(true);
    setError('');
    try {
      await deleteCycle(id);
      await loadData();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  // ─── Scorecard mini-form ──────────────────────────────────────────────────────

  const [scForm, setScForm] = useState({ unitId: '', unitName: '', score: 3, memberIds: '' });
  const handleAddScorecard = async () => {
    if (!selectedCycleId || !scForm.unitId || !scForm.unitName) return;
    setBusy(true);
    setError('');
    try {
      await upsertTeamScorecard({
        cycleId: selectedCycleId,
        unitId: scForm.unitId,
        unitName: scForm.unitName,
        score: scForm.score,
        setBy: user!.id,
        memberIds: scForm.memberIds.split(',').map(s => s.trim()).filter(Boolean),
      });
      await loadScorecards(selectedCycleId);
      setScForm({ unitId: '', unitName: '', score: 3, memberIds: '' });
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Review Cycles</h1>
        <button
          onClick={openCreate}
          className="bg-brand-accent hover:bg-blue-500 text-gray-900 text-sm font-semibold px-4 py-2 rounded-lg transition"
        >
          + New Cycle
        </button>
      </div>

      {activateResult && (
        <div className="bg-green-900/40 border border-green-500/30 rounded-lg p-3 text-green-300 text-sm">
          {activateResult}
        </div>
      )}

      {error && <p className="text-red-600 text-sm">{error}</p>}

      {/* Cycle list */}
      <div className="space-y-3">
        {cycles.map(c => (
          <div
            key={c.id}
            className="bg-white border border-gray-200 shadow-sm rounded-xl p-4"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-semibold text-gray-900">{c.name}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLOR[c.status]}`}>
                    {c.status}
                  </span>
                  {c.frameworkVersion === 'hr_v1' && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
                      hr_v1 · Step {c.currentStep ?? '—'}/7
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  {c.year} · {c.period} ·
                  Start: {formatTs(c.startDate)} ·
                  End: {formatTs(c.endDate)}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  Template: {templates.find(t => t.id === c.templateId)?.name ?? c.templateId}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {c.frameworkVersion === 'hr_v1' && (
                  <Link
                    to={routes.hrV1Cycle(c.id)}
                    className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white rounded px-3 py-1 transition inline-flex items-center gap-1"
                  >
                    <span className="material-symbols-rounded" style={{ fontSize: 14 }} aria-hidden="true">play_arrow</span>
                    Open workflow
                  </Link>
                )}
                {STATUS_NEXT[c.status] && (
                  <button
                    onClick={() => handleTransition(c)}
                    disabled={busy}
                    className="text-xs bg-brand-accent/20 hover:bg-brand-accent/40 border border-brand-accent/40 text-blue-600 rounded px-3 py-1 transition"
                  >
                    {STATUS_NEXT[c.status]?.label}
                  </button>
                )}
                <button
                  onClick={() => setSelectedCycleId(selectedCycleId === c.id ? null : c.id)}
                  className="text-xs text-gray-500 hover:text-gray-900 border border-gray-200 rounded px-2 py-1"
                >
                  Scorecards {selectedCycleId === c.id ? '▲' : '▼'}
                </button>
                <button onClick={() => openEdit(c)} className="text-xs text-blue-600 hover:underline">Edit</button>
                <button onClick={() => handleDeleteCycle(c.id)} className="text-xs text-red-600 hover:underline">Delete</button>
              </div>
            </div>

            {/* Team Scorecards panel */}
            {selectedCycleId === c.id && (
              <div className="mt-4 border-t border-gray-200 pt-4 space-y-3">
                <p className="text-xs font-semibold text-gray-500 uppercase">Team Scorecards</p>
                {scorecards.length === 0 && (
                  <p className="text-xs text-gray-400">No scorecards yet for this cycle.</p>
                )}
                {scorecards.map(sc => (
                  <div key={sc.id} className="flex items-center justify-between bg-gray-50 rounded p-2">
                    <span className="text-sm text-gray-900">{sc.unitName}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-emerald-600 font-bold">{sc.score} / 5</span>
                      <button
                        onClick={async () => { await deleteTeamScorecard(sc.id); loadScorecards(c.id); }}
                        className="text-xs text-red-600 hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
                {/* Add scorecard */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-2">
                  <input value={scForm.unitId} onChange={e => setScForm({ ...scForm, unitId: e.target.value })} placeholder="Unit ID" className={miniInput} />
                  <input value={scForm.unitName} onChange={e => setScForm({ ...scForm, unitName: e.target.value })} placeholder="Unit Name" className={miniInput} />
                  <input type="number" min={1} max={5} step={0.1} value={scForm.score} onChange={e => setScForm({ ...scForm, score: Number(e.target.value) })} placeholder="Score (1-5)" className={miniInput} />
                  <input value={scForm.memberIds} onChange={e => setScForm({ ...scForm, memberIds: e.target.value })} placeholder="Member UIDs (comma)" className={miniInput} />
                  <button onClick={handleAddScorecard} className="bg-brand-accent text-gray-900 text-xs rounded px-3 py-1.5">
                    Add
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
        {cycles.length === 0 && (
          <p className="text-gray-400 text-sm text-center py-10">No cycles yet.</p>
        )}
      </div>

      {/* Create/Edit form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="bg-white border border-gray-200 rounded-2xl p-6 w-full max-w-lg space-y-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold text-gray-900">{editCycle ? 'Edit Cycle' : 'New Cycle'}</h2>
            <FormRow label="Name">
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="e.g. H1 2025 Performance Review" />
            </FormRow>
            <div className="grid grid-cols-2 gap-3">
              <FormRow label="Year">
                <input type="number" value={form.year} onChange={e => setForm({ ...form, year: Number(e.target.value) })} className={inputCls} />
              </FormRow>
              <FormRow label="Period">
                <select value={form.period} onChange={e => setForm({ ...form, period: e.target.value as CyclePeriod })} className={inputCls}>
                  {(['H1','H2','Q1','Q2','Q3','Q4','annual'] as CyclePeriod[]).map(p => <option key={p}>{p}</option>)}
                </select>
              </FormRow>
            </div>
            <FormRow label="Framework version">
              <select
                value={form.frameworkVersion}
                onChange={e => setForm({ ...form, frameworkVersion: e.target.value as 'optics' | 'hr_v1' })}
                className={inputCls}
                disabled={!!editCycle}
              >
                <option value="optics">OPTICS (legacy)</option>
                <option value="hr_v1">HR Framework v1 (KPI/OKR/Core/Leadership + 360 + Appraisal + Calibration)</option>
              </select>
              {form.frameworkVersion === 'hr_v1' && (
                <p className="text-xs text-stone-500 mt-1">
                  After creation, open the cycle via "Open workflow" to run the 7-step process.
                </p>
              )}
            </FormRow>
            <FormRow label="OPTICS Template (legacy only)">
              <select
                value={form.templateId}
                onChange={e => setForm({ ...form, templateId: e.target.value })}
                className={inputCls}
              >
                <option value="">— select template —</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              {form.frameworkVersion === 'hr_v1' && (
                <p className="text-xs text-stone-500 mt-1">
                  Ignored for hr_v1 cycles — uses <code>default-hr-v1</code> automatically.
                </p>
              )}
            </FormRow>
            <div className="grid grid-cols-2 gap-3">
              <FormRow label="Prep Window Start">
                <input type="date" value={form.prepWindowStart} onChange={e => setForm({ ...form, prepWindowStart: e.target.value })} className={inputCls} />
              </FormRow>
              <FormRow label="Review Start Date">
                <input type="date" value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} className={inputCls} />
              </FormRow>
              <FormRow label="Lock Date">
                <input type="date" value={form.lockDate} onChange={e => setForm({ ...form, lockDate: e.target.value })} className={inputCls} />
              </FormRow>
              <FormRow label="End Date">
                <input type="date" value={form.endDate} onChange={e => setForm({ ...form, endDate: e.target.value })} className={inputCls} />
              </FormRow>
            </div>
            {error && <p className="text-red-600 text-sm">{error}</p>}
            <div className="flex gap-3">
              <button onClick={handleSave} disabled={busy} className="bg-brand-accent hover:bg-blue-500 text-gray-900 text-sm font-semibold px-5 py-2 rounded-lg transition disabled:opacity-50">
                {busy ? 'Saving…' : editCycle ? 'Update' : 'Create'}
              </button>
              <button onClick={resetForm} className="text-gray-500 hover:text-gray-900 text-sm px-4 py-2">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FormRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="text-xs text-gray-500 font-medium block mb-1">{label}</label>
    {children}
  </div>
);

const inputCls = 'w-full bg-white text-gray-900 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:border-brand-accent focus:outline-none';
const miniInput = 'bg-white text-gray-900 text-xs border border-gray-200 rounded px-2 py-1 focus:border-brand-accent focus:outline-none';

/**
 * Firestore Timestamps can arrive as a Timestamp instance, a serialised
 * {seconds, nanoseconds} object (after JSON round-trip), or a raw Date.
 * One narrow union here keeps every callsite type-safe.
 */
type TimestampLike =
  | { toDate: () => Date }
  | { seconds: number; nanoseconds?: number }
  | Date
  | null
  | undefined;

function tsToDate(ts: TimestampLike): Date | null {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  if ('toDate' in ts && typeof ts.toDate === 'function') return ts.toDate();
  if ('seconds' in ts && typeof ts.seconds === 'number') return new Date(ts.seconds * 1000);
  return null;
}

function tsToInput(ts: TimestampLike): string {
  const d = tsToDate(ts);
  return d ? format(d, 'yyyy-MM-dd') : '';
}

function formatTs(ts: TimestampLike): string {
  const d = tsToDate(ts);
  return d ? format(d, 'dd MMM yyyy') : '—';
}

export default CycleManagement;
